import { ChangeDetectionStrategy, Component, computed, effect, inject, input, ViewEncapsulation } from '@angular/core';
import { HxIcon } from '../icon/icon';
import { HxTooltipDirective } from '../tooltip/tooltip';
import { StatusIndicatorService } from './status-indicator.service';
import { StatusIndicatorColor, StatusSource } from './status-indicator.types';

export type StatusIndicatorSize = 'sm' | 'md' | 'lg';

/**
 * Status Indicator Component
 *
 * Displays app connectivity status (Online/Offline) using a color-coded chip with icon and tooltip. Read-only component
 * that subscribes to StatusIndicatorService singleton for state.
 *
 * @example
 *   ```html
 *   <!-- Default: primary (brand) online, amber offline, medium size, with icon -->
 *   <hx-status-indicator></hx-status-indicator>
 *
 *   <!-- Size variants -->
 *   <hx-status-indicator size="sm"></hx-status-indicator>
 *   <hx-status-indicator size="lg"></hx-status-indicator>
 *
 *   <!-- Color overrides -->
 *   <hx-status-indicator onlineColor="success" offlineColor="error"></hx-status-indicator>
 *
 *   <!-- Hidden mode (not rendered) -->
 *   <hx-status-indicator statusSource="hidden"></hx-status-indicator>
 *
 *   <!-- Custom labels and tooltip -->
 *   <hx-status-indicator
 *     onlineLabel="Connected"
 *     offlineLabel="Disconnected"
 *     onlineTooltip="All systems operational"
 *     offlineTooltip="Connection lost"
 *   ></hx-status-indicator>
 *
 *   <!-- Custom debounce time (in milliseconds) -->
 *   <hx-status-indicator [debounceMs]="1000"></hx-status-indicator>
 *   ```;
 *
 * @see {@link https://github.com/ComplexCare/web.library/specs/041-status-indicator/spec.md Specification}
 */
@Component({
  selector: 'hx-status-indicator',
  standalone: true,
  imports: [HxIcon, HxTooltipDirective],
  templateUrl: './status-indicator.html',
  styleUrls: ['./status-indicator.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HxStatusIndicator {
  private readonly service = inject(StatusIndicatorService);

  // Inputs
  readonly onlineLabel = input<string>('Online');
  readonly offlineLabel = input<string>('Offline');
  readonly onlineTooltip = input<string>('You are online. Changes are being saved normally.');
  readonly offlineTooltip = input<string>('You are offline. Some actions may be limited until connection is restored.');
  readonly size = input<StatusIndicatorSize>('md');
  readonly showIcon = input<boolean>(true);
  readonly statusSource = input<StatusSource>('auto');
  readonly onlineColor = input<StatusIndicatorColor>('primary');
  readonly offlineColor = input<StatusIndicatorColor>('warning');
  readonly debounceMs = input<number>(500);

  // Computed signals
  // Reuse the service-provided read-only signal directly to avoid creating
  // an extra computed wrapper. The service already exposes `isOnline: Signal<boolean>`.
  readonly isOnline = this.service.isOnline;
  readonly currentLabel = computed(() => (this.isOnline() ? this.onlineLabel() : this.offlineLabel()));
  readonly currentTooltip = computed(() => (this.isOnline() ? this.onlineTooltip() : this.offlineTooltip()));
  readonly isIndicatorHidden = computed(() => this.statusSource() === 'hidden');

  readonly hostClasses = computed(() => {
    const baseClass = 'hx-status-indicator';
    const classes: string[] = [baseClass];

    // Size variant
    classes.push(`${baseClass}--${this.size()}`);

    if (this.isIndicatorHidden()) {
      classes.push(`${baseClass}--hidden`);
    }

    if (this.isOnline()) {
      classes.push(`${baseClass}--online`);
      classes.push(`${baseClass}--online-${this.onlineColor()}`);
    } else {
      classes.push(`${baseClass}--offline`);
      classes.push(`${baseClass}--offline-${this.offlineColor()}`);
    }

    return classes.join(' ');
  });

  constructor() {
    // Start listening to network events on component instantiation
    this.service.start();

    // Watch for debounceMs changes and reconfigure service
    effect(() => {
      this.service.configure({ debounceMs: this.debounceMs() });
    });
  }
}
